import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUp, BadgeCheck, Database, Mail, Scale, Shield } from "lucide-react";
import MarketingLayout from "../marketing/MarketingLayout";
import SiteMeta from "../marketing/SiteMeta";
import { LEGAL_DOCS } from "./legalDocs";
import { MARKETING_ROUTES } from "../marketing/routes";
import "./legalPage.css";
import "./privacyPolicy.css";
import "./termsOfUse.css";

const POLICY = LEGAL_DOCS.terms;

const SNAPSHOT = [
  { icon: BadgeCheck, label: "Eligibility", value: "18 years of age and authority to bind your organisation" },
  { icon: Shield, label: "Privacy", value: "The Privacy Policy forms an integral part of these Terms" },
  { icon: Database, label: "Customer Data", value: "You retain ownership of Customer Data" },
  { icon: Scale, label: "Governing law", value: "Laws of India · courts in Bengaluru, Karnataka" },
];

const TOC_GROUPS = [
  { label: "Access and use", start: 1, end: 8 },
  { label: "Data, IP and privacy", start: 9, end: 16 },
  { label: "Availability and liability", start: 17, end: 25 },
  { label: "Notices, law and contact", start: 26, end: 38 },
];

function sectionAnchor(heading) {
  return `section-${String(heading).split(" ")[0].replace(/\./g, "")}`;
}

function splitHeading(heading) {
  const match = String(heading).match(/^(\d+)\.\s*(.*)$/);
  return match ? { num: match[1], title: match[2] } : { num: "", title: heading };
}

function PolicyText({ text }) {
  if (!text) return null;
  const parts = text.split(/(Privacy Policy|Terms of Use \/ Terms of Service|hr@brixigo.com)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part === "Privacy Policy") {
          return (
            <Link key={i} to={MARKETING_ROUTES.privacy}>
              Privacy Policy
            </Link>
          );
        }
        if (part === "Terms of Use / Terms of Service") {
          return <strong key={i}>{part}</strong>;
        }
        if (part === "hr@brixigo.com") {
          return (
            <a key={i} href="mailto:hr@brixigo.com">
              hr@brixigo.com
            </a>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function Blocks({ paragraphs = [], list, ordered }) {
  const ListTag = ordered ? "ol" : "ul";
  return (
    <>
      {paragraphs.map((p, i) => (
        <p key={`p-${i}`}>
          <PolicyText text={p} />
        </p>
      ))}
      {list?.length ? (
        <ListTag className={`legal-list${ordered ? " legal-list-ol" : ""}`}>
          {list.map((item, i) => (
            <li key={`${i}-${item.slice(0, 40)}`}>
              <PolicyText text={item} />
            </li>
          ))}
        </ListTag>
      ) : null}
    </>
  );
}

function ContactBlock({ contact }) {
  if (!contact) return null;
  return (
    <address className="pp-contact legal-contact">
      {contact.name ? <strong>{contact.name}</strong> : null}
      {contact.emailLabel ? <p className="pp-contact-label">{contact.emailLabel}</p> : null}
      {contact.email ? (
        <p>
          {contact.emailPrefix || "Email"}: <a href={`mailto:${contact.email}`}>{contact.email}</a>
        </p>
      ) : null}
      {contact.phone ? (
        <p>
          Phone: <a href={contact.phoneHref || `tel:${contact.phone}`}>{contact.phone}</a>
        </p>
      ) : null}
      {contact.address ? (
        <p>
          {contact.addressLabel ? `${contact.addressLabel}: ` : "Address: "}
          {contact.address}
        </p>
      ) : null}
    </address>
  );
}

export default function TermsOfUsePage() {
  const location = useLocation();
  const tocListRef = useRef(null);
  const visibleIds = useRef(new Set());
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState(sectionAnchor(POLICY.sections[0].heading));
  const [query, setQuery] = useState("");
  const [tocOpen, setTocOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);

  const sectionIds = useMemo(
    () => POLICY.sections.map((section) => sectionAnchor(section.heading)),
    []
  );

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return POLICY.sections;
    return POLICY.sections.filter((section) => section.heading.toLowerCase().includes(q));
  }, [query]);

  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 0);
      setShowTop(window.scrollY > 480);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const nodes = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleIds.current.add(entry.target.id);
          else visibleIds.current.delete(entry.target.id);
        });
        const next = sectionIds.find((id) => visibleIds.current.has(id));
        if (next) setActiveId(next);
      },
      { rootMargin: "-120px 0px -58% 0px", threshold: 0 }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [sectionIds]);

  useEffect(() => {
    const link = tocListRef.current?.querySelector(`[data-id="${activeId}"]`);
    link?.scrollIntoView({ block: "nearest" });
  }, [activeId]);

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (!hash) return;
    const timer = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const scrollToSection = (event, id) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
    setActiveId(id);
    setTocOpen(false);
  };

  const searching = Boolean(query.trim());

  return (
    <MarketingLayout className="legal-page-shell pp-page tou-page">
      <SiteMeta title={POLICY.pageTitle} description={POLICY.description} />

      <div className="pp-progress" aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </div>

      <header className="pp-hero">
        <div className="pp-hero-inner">
          <p className="pp-kicker">Legal</p>
          <h1>{POLICY.title}</h1>
          <p className="pp-dates">
            <span>Effective Date: {POLICY.effectiveDate}</span>
            <span>Last Updated: {POLICY.updated}</span>
          </p>
          {POLICY.intro.map((p, i) => (
            <p
              key={p.slice(0, 48)}
              className={i === 0 ? "pp-lead" : i === POLICY.intro.length - 1 ? "pp-callout" : "pp-intro"}
            >
              <PolicyText text={p} />
            </p>
          ))}
          <div className="pp-facts">
            {SNAPSHOT.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.label} className="pp-fact">
                  <div className="pp-fact-icon">
                    <Icon size={16} strokeWidth={2.2} />
                  </div>
                  <p>
                    {item.label}
                    <strong>{item.value}</strong>
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </header>

      <div className="pp-layout">
        <nav className={`pp-toc${tocOpen ? " is-open" : ""}`} aria-label="Terms of Use sections">
          <div className="pp-toc-head">
            <p className="pp-toc-title">Contents</p>
            <button
              type="button"
              className="pp-toc-toggle"
              onClick={() => setTocOpen((open) => !open)}
              aria-expanded={tocOpen}
            >
              {tocOpen ? "Hide" : "Jump to"}
            </button>
          </div>
          <input
            className="pp-toc-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a section"
            aria-label="Find a terms section"
          />
          <ol ref={tocListRef}>
            {filteredSections.map((section) => {
              const id = sectionAnchor(section.heading);
              const { num, title } = splitHeading(section.heading);
              const sectionNum = Number(num);
              const group = !searching
                ? TOC_GROUPS.find((g) => g.start === sectionNum)
                : null;
              return (
                <li key={section.heading}>
                  {group ? <p className="tou-toc-group">{group.label}</p> : null}
                  <a
                    href={`#${id}`}
                    data-id={id}
                    className={activeId === id ? "is-active" : ""}
                    onClick={(event) => scrollToSection(event, id)}
                  >
                    {num ? <span className="pp-toc-num">{num}</span> : null}
                    <span>{title}</span>
                  </a>
                </li>
              );
            })}
          </ol>
          {!filteredSections.length ? <p className="pp-toc-empty">No matching sections.</p> : null}
        </nav>

        <article className="legal-page pp-article">
          {POLICY.sections.map((section) => (
            <section
              key={section.heading}
              id={sectionAnchor(section.heading)}
              className="legal-section"
            >
              <h2>{section.heading}</h2>
              <Blocks paragraphs={section.paragraphs} list={section.list} ordered={section.ordered} />
              {section.subsections?.map((sub) => (
                <div key={sub.heading} id={sectionAnchor(sub.heading)} className="legal-subsection">
                  <h3>{sub.heading}</h3>
                  <Blocks paragraphs={sub.paragraphs} list={sub.list} ordered={sub.ordered} />
                </div>
              ))}
              <ContactBlock contact={section.contact} />
              <Blocks paragraphs={section.afterParagraphs} />
            </section>
          ))}

          <p className="pp-footer-nav legal-back">
            <Link to={MARKETING_ROUTES.privacy}>Privacy Policy</Link>
            <Link to={MARKETING_ROUTES.contact}>Contact Us</Link>
            <a href="mailto:hr@brixigo.com">
              <Mail size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />
              hr@brixigo.com
            </a>
          </p>
        </article>
      </div>

      <button
        type="button"
        className={`pp-top${showTop ? " is-visible" : ""}`}
        aria-label="Back to top"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <ArrowUp size={18} />
      </button>
    </MarketingLayout>
  );
}
